import { Timestamp } from 'firebase/firestore';
import { getBalanceFlowsByStatusesAndAccount } from '@sp-firebase/balanceFlowFB';
import { getAllFutureOrdersByAccount } from '@sp-firebase/futureOrderFB';
import { getAllInventoryReportOfAccount } from '@sp-firebase/inventoryReportFB';
import { getAllProductsByAccountId } from '@sp-firebase/productFB';
import { DashboardNotification, NotificationTypes } from '@sp-types/notificationType';
import { StatusType } from '@sp-types/statusType';
import { ADD_FUTURE_ORDER_ROOT_ROUTE } from '../../../FutureOrder/AddNewFutureOrder/AddNewFutureOrderUtils';
import { getSentFileBatches } from '@sp-firebase/fileBatchesFB';

export const OPEN_STATUS_TYPES: StatusType[] = ['pending', 'underwriting'];

export const getAllInProcess = async (accountId: string, statuses?: StatusType[]): Promise<DashboardNotification[]> => {
  const [openFileBatches, openFutureOrders, openInventoryReports, openProducts, openBalanceFlows] = await Promise.all([
    getAndMapSentFileBatches(accountId),
    getOpenFutureOrdersNotifications(accountId, statuses || OPEN_STATUS_TYPES),
    getOpenInventoryNotifications(accountId, statuses || OPEN_STATUS_TYPES),
    getOpenProducts(accountId, statuses || OPEN_STATUS_TYPES),
    getOpenBalanceFlows(accountId, statuses || OPEN_STATUS_TYPES),
  ]);
  return [...openFileBatches, ...openInventoryReports, ...openFutureOrders, ...openProducts, ...openBalanceFlows];
};

const fileBatchMsg: Record<string, string> = {
  pending: 'Files have been submitted.',
  done: 'Files have been processed and uploaded.',
  edit: 'Files are awaiting customer review.',
  draft: 'Files have not been submitted.',
  '': '',
};

const fileBatchDescMsg: Record<string, string> = {
  pending: 'Files have been submitted and are currently being processed.',
  done: 'Files were successfully processed and uploaded.',
  edit: 'Files were reviewed by staff and are awaiting customer clarification.',
  draft: 'Files have not been submitted yet.',
  '': '',
};

const statusToType: Record<string, string> = { pending: 'pending', edit: 'action', done: 'info' };
const getAndMapSentFileBatches = async (accountId: string): Promise<DashboardNotification[]> => {
  const fileBatches = await getSentFileBatches(accountId);

  const dashInProcs =
    fileBatches
      ?.filter((f) => !f.dismissAt)
      .map(({ id, createdAt, status, files, resultFile, comments }) => ({
        id,
        entity: 'fileBatch',
        date: createdAt,
        text: fileBatchMsg[status],
        type: statusToType[status] as NotificationTypes,
        descriptionText: fileBatchDescMsg[status],
        formulaId: [],
        status: status as StatusType,
        actionText: status === 'edit' ? 'continue' : undefined,
        actionUrl: status === 'edit' ? `/upload-files/${id}` : undefined,
        options: { files, isFileRecord: true, id, createdAt, resultFile, status, comments } as any,
      })) || [];

  return dashInProcs;
};

const getOpenFutureOrdersNotifications = async (
  accountId: string,
  statuses: StatusType[],
): Promise<DashboardNotification[]> => {
  const openFutureOrders = await getAllFutureOrdersByAccount(accountId, statuses);
  if (!openFutureOrders || !openFutureOrders.length) return [];
  return openFutureOrders
    ?.filter((item) => !item.dismissAt)
    .map((item) =>
      inProcessDict(
        item.uuid || '',
        item.totalExposure?.map((item) => item.formulaId) || [],
        item.updatedAt,
        'futureOrders',
        item.status,
        { fileUrl: item.fileUrl?.[0] || '', itemType: 'futureOrder', orderId: item.orderId || item.uuid },
      ),
    );
};

const getOpenInventoryNotifications = async (
  accountId: string,
  statuses: StatusType[],
): Promise<DashboardNotification[]> => {
  const openInventoryReports = await getAllInventoryReportOfAccount(accountId, statuses);
  if (!openInventoryReports || !openInventoryReports.length) return [];
  return openInventoryReports
    .filter((item) => item.id && !item.dismissAt)
    .map(({ id, materialId, reportingTime, status, fileUrl }) =>
      inProcessDict(id!, [materialId], reportingTime, 'inventoryReports', status, { fileUrl, itemType: 'inventory' }),
    );
};

const getOpenProducts = async (accountId: string, statuses: StatusType[]): Promise<DashboardNotification[]> => {
  const openProducts = await getAllProductsByAccountId(accountId, statuses);
  if (!openProducts || !openProducts.length) return [];
  return openProducts
    .filter((item) => !item.dismissAt)
    .map((item) =>
      inProcessDict(
        item.uuid || '',
        item.ingredients.map((item) => item.materialID),
        item.updatedAt,
        'products',
        item.status,
        { fileUrl: item.filesUrl?.[0] || '', itemType: 'product' },
      ),
    );
};

const getOpenBalanceFlows = async (accountId: string, statuses: StatusType[]): Promise<DashboardNotification[]> => {
  const openBalanceFlows = await getBalanceFlowsByStatusesAndAccount(accountId, statuses);
  if (!openBalanceFlows || !openBalanceFlows.length) return [];
  return openBalanceFlows
    .filter((item) => !item.dismissAt)
    .map((item) =>
      inProcessDict(item.id, [item.formulaId], item.updatedAt || Timestamp.now(), 'balanceFlow', item.status, {
        itemType: 'balanceFlow',
      }),
    );
};

const inProcessDict = (
  id: string,
  materialIds: string[],
  date: Timestamp,
  processType: inProcessTypes,
  status: StatusType,
  options?: any,
): DashboardNotification => {
  return status === 'pending'
    ? pendingProcessesDict(id, materialIds, date, status, options)[processType]
    : status === 'underwriting'
    ? underWritingProcessesDict(id, materialIds, date, status, options)[processType]
    : underWritingProcessesDict(id, materialIds, date, status, options)[processType];
};

const pendingProcessesDict = (
  id: string,
  materialIds: string[],
  date: Timestamp,
  status: StatusType,
  options?: any,
): Record<inProcessTypes, DashboardNotification> => {
  return {
    balanceFlow: {
      id,
      date,
      text: `Balance for ${materialIds?.[0]} is pending.`,
      type: 'pending',
      descriptionText: 'Balance has been submitted and is awaiting approval.',
      formulaId: materialIds,
      actionText: 'continue',
      actionUrl: `/balance-risk/${id}`,
      status,
      options,
      entity: 'balanceFlow',
    },
    inventoryReports: {
      id,
      date,
      text: `Inventory report ${getFileName(id, options?.fileUrl)} is pending.`,
      type: 'pending',
      descriptionText: 'Inventory report has been submitted and is awaiting approval.',
      formulaId: materialIds,
      actionText: 'continue',
      actionUrl: `/inventory`,
      status,
      options,
      entity: 'inventoryReport',
    },
    products: {
      id,
      date,
      text: `Product ${getFileName(id, options?.fileUrl)} is pending.`,
      type: 'pending',
      descriptionText: 'New product has been submitted and is awaiting approval.',
      formulaId: materialIds,
      actionText: 'continue',
      actionUrl: `/setting-product-create/${id}`,
      status,
      options,
      entity: 'product',
    },
    futureOrders: {
      id,
      date,
      text: `Future order ${getFileName(options?.orderId || id, options?.fileUrl)} is pending.`,
      type: 'pending',
      descriptionText: 'Future order file has been submitted and is awaiting approval.',
      formulaId: materialIds,
      actionText: 'continue',
      actionUrl: `/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${id}/fill-order-details`,
      status,
      options,
      entity: 'futureOrder',
    },
  };
};

const underWritingProcessesDict = (
  id: string,
  materialIds: string[],
  date: Timestamp,
  status: StatusType,
  options?: any,
): Record<inProcessTypes, DashboardNotification> => {
  return {
    balanceFlow: {
      id,
      date,
      text: `Balance for ${materialIds?.[0]} is awaiting your action.`,
      type: 'action',
      descriptionText: 'The balance has been approved by our team. Click below to proceed.',
      formulaId: materialIds,
      actionText: 'continue',
      actionUrl: `/balance-risk/${id}`,
      status,
      options,
      entity: 'balanceFlow',
    },
    inventoryReports: {
      id,
      date,
      text: `Inventory report ${id} is awaiting your action.`,
      type: 'action',
      descriptionText: 'The inventory report has been approved by our team. Click below to proceed.',
      formulaId: materialIds,
      actionText: 'continue',
      actionUrl: `/inventory`,
      status,
      options,
      entity: 'inventoryReport',
    },
    products: {
      id,
      date,
      text: `Product ${getFileName(id, options?.fileUrl)} is awaiting your action.`,
      type: 'action',
      descriptionText: 'The product has been approved by our team. Click below to proceed.',
      formulaId: materialIds,
      actionText: 'continue',
      actionUrl: `/setting-product-create/${id}`,
      status,
      options,
      entity: 'product',
    },
    futureOrders: {
      id,
      date,
      text: `Future order ${getFileName(options?.orderId || id, options?.fileUrl)} is awaiting your action.`,
      descriptionText: 'The future order has been approved by our team. Click below to proceed.',
      type: 'action',
      formulaId: materialIds,
      actionText: 'continue',
      actionUrl: `/${ADD_FUTURE_ORDER_ROOT_ROUTE}/${id}/order-summary`,
      status,
      options,
      entity: 'futureOrder',
    },
  };
};

const extractFileRegex = /([^/]+)_\d*(\.[\w]+)$/;

export const getFileName = (id: string, fileUrl?: string, onlyFilename?: boolean): string => {
  const match = fileUrl?.match(extractFileRegex);
  const fileName = match ? match[1] + match[2] : id;

  return onlyFilename ? fileName : `<file: /${fileName}>`;
};
type inProcessTypes = 'balanceFlow' | 'inventoryReports' | 'products' | 'futureOrders';
